<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import DashboardShell from '@/components/dashboard/shell/DashboardShell.vue'
import DashboardLegalModal from '@/components/dashboard/modals/DashboardLegalModal.vue'
import UpgradeModal from '@/components/dashboard/modals/UpgradeModal.vue'
import DashboardToast from '@/components/dashboard/ui/DashboardToast.vue'
import AddDonationModal from '@/components/dashboard/modals/AddDonationModal.vue'
import TaxPackExportModal from '@/components/dashboard/modals/TaxPackExportModal.vue'
import DonationDetailModal from '@/components/dashboard/modals/DonationDetailModal.vue'
import RecurringDonationModal from '@/components/dashboard/modals/RecurringDonationModal.vue'
import ManageAuthorisationModal from '@/components/dashboard/modals/ManageAuthorisationModal.vue'
import RenewAuthorisationModal from '@/components/dashboard/modals/RenewAuthorisationModal.vue'
import CloseAccountModal from '@/components/dashboard/modals/CloseAccountModal.vue'
import DeleteLinkedAccountModal from '@/components/dashboard/modals/DeleteLinkedAccountModal.vue'
import Statement from '@/components/views/dashboard/statements/Statement.vue'
import SaSpinner from '@/components/dashboard/ui/SaSpinner.vue'
import { useUpgradeReturn } from '@/composables/useUpgradeReturn'
import { scrollWindowToTopSoon } from '@/helpers/scrollWindowToTop'

const store = useStore()
useUpgradeReturn()

/**
 * Vue does not await async onBeforeMount — child views can mount and clear
 * isLoading before fetches settle. Gate content until auth + plan are known.
 */
const shellDataReady = ref(false)
const isLoading = computed(() => store.state.isLoading)
const showPageLoader = computed(() => !shellDataReady.value || isLoading.value)

watch(showPageLoader, async (loading) => {
  if (loading) return
  // Content just became visible after Auth → Dashboard; nudge scroll again
  // after paint so browser restoration / focus cannot leave us mid-page.
  await nextTick()
  scrollWindowToTopSoon()
})

onBeforeMount(async () => {
  document.body.className = 'dashboard-layout'
  store.commit('SET_LOADING', true)
  try {
    // Parallel: Gift Aid authorisation and Swiftaid Giving plan drive most
    // free-vs-paid / authorised UI — resolve both before mounting page content.
    await Promise.all([
      store.dispatch('fetchAuth'),
      store.dispatch('fetchDonorPlan'),
    ])
  } finally {
    shellDataReady.value = true
  }
})
</script>

<template>
  <DashboardShell>
    <div
      v-if="showPageLoader"
      class="sa-page-loader"
      role="status"
      aria-live="polite"
      :aria-label="$t('Accessibility.Button.Loading')"
    >
      <SaSpinner size="page" />
    </div>
    <!-- Mount page content only after auth + plan are known to avoid flash of free/unauth UI -->
    <router-view v-if="shellDataReady" />
    <DashboardLegalModal />
    <UpgradeModal />
    <DashboardToast />
    <AddDonationModal />
    <TaxPackExportModal />
    <DonationDetailModal />
    <RecurringDonationModal />
    <ManageAuthorisationModal />
    <RenewAuthorisationModal />
    <CloseAccountModal />
    <DeleteLinkedAccountModal />
    <Statement />
  </DashboardShell>
</template>
